import { ComponentProps, createRef } from 'preact';
import { useCallback, useMemo, useRef, useState } from 'preact/hooks';
import { classNames } from '../utils/UNSAFE_classNames';
import { TestIdProps } from '../hooks/UNSAFE_useTestId';
import {
  OverflowTabBarItem,
  RemovableTabBarItem,
  TabBar,
  TabBarItem,
  TabBarLinkItem
} from '../UNSAFE_TabBar';
import { CloseDetail, Item } from '../UNSAFE_TabBarCommon';
import { styles } from './themes/OverflowTabBarStyles.css';
import { useDetectHorizontalOverflow } from '../hooks/PRIVATE_useDetectHorizontalOverflow';

type OverflowTabBarProps<K extends string | number> = {
  /**
   * An array of data to render individual tabs
   */
  items: Item<K>[];
  /**
   * The truncation configuration for tab labels.
   * 'progressive' will restrict the width of each tab label to allow all tabs to fit if enough space is not available to display all of the tabs.
   * Labels that are truncated are displayed with ellipses. However the width of each tab label will not be truncated below a specific threshold.
   * 'none' tabs always take up the space needed by the labels even if enough space is not available to display all of the tabs.
   */
  truncation?: 'none' | 'progressive';
} & Omit<ComponentProps<typeof TabBar>, 'children' | 'aria-describedby' | 'ref'>;

/**
 * OverflowTabBar handles the rendering of overflowing tab items.
 */
export function OverflowTabBar<K extends string | number>({
  items,
  layout = 'stretch',
  display = 'standard',
  size = 'md',
  edge = 'top',
  selection,
  truncation,
  onSelect,
  onRemove,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  contextMenuConfig,
  testId
}: OverflowTabBarProps<K> & TestIdProps) {
  const displayStackProps = {
    display: display
  };
  const displayNonStackProps = {
    display: display,
    size: size
  };

  const displayProps = display === 'stacked' ? displayStackProps : displayNonStackProps;
  const rootRef = useRef<HTMLDivElement>(null);
  const tabBarRef = createRef<HTMLDivElement>();

  const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false);

  const isOutsideMouseDown = useRef<boolean>(false);

  const handleClose = (e: CloseDetail) => {
    if (tabBarRef.current && ['itemAction', 'dismissed'].includes(e.reason)) {
      tabBarRef?.current?.focus();
    }
    if (e.reason === 'outsideMousedown') {
      isOutsideMouseDown.current = true;
    }
    setIsDropDownOpen(false);
  };

  const handleSelect = useCallback<Required<ComponentProps<typeof TabBar>>['onSelect']>(
    (e: { value: string | number }) => {
      if (e.value === 'more') {
        //If overflow item is clicked after dropdown is open, the reason is outsideMouseDown and it needs to be closed
        if (isOutsideMouseDown.current) {
          setIsDropDownOpen(false);
        } else {
          setIsDropDownOpen(true);
        }
      } else {
        onSelect?.(e);
      }
      isOutsideMouseDown.current = false;
    },
    [onSelect]
  );

  const overflowItemKey = 'more' as K;

  const keysArray = useMemo(() => items.map((item) => item.itemKey) as K[], [items]);

  const { visibleItemKeys, overflowItemKeys, maxWidths } = useDetectHorizontalOverflow({
    rootRef: tabBarRef,
    keysArray,
    isTruncationDisabled: !(truncation && truncation === 'progressive')
  });

  const overflowTabBarClass = classNames([styles.overflowTabBarBase]);

  return (
    <div ref={rootRef} class={overflowTabBarClass}>
      <TabBar
        ref={tabBarRef}
        selection={selection}
        onSelect={handleSelect}
        {...displayProps}
        layout={layout}
        edge={edge}
        onRemove={onRemove}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        testId={testId}
        contextMenuConfig={contextMenuConfig}>
        {visibleItemKeys != null &&
          visibleItemKeys.length > 0 &&
          items
            .filter((item) => visibleItemKeys?.includes(item.itemKey))
            .map((item: Item<K>) =>
              item.href != null ? (
                <TabBarLinkItem
                  href={item.href}
                  itemKey={item.itemKey}
                  label={item.label}
                  icon={item.icon}
                  badge={item.badge}
                  metadata={item.metadata}
                  severity={item.severity}
                  aria-controls={item.tabPanelId}
                  labelMaxWidth={maxWidths?.get(item.itemKey)}
                />
              ) : item.isRemovable ? (
                <RemovableTabBarItem
                  itemKey={item.itemKey}
                  label={item.label}
                  icon={item.icon}
                  badge={item.badge}
                  metadata={item.metadata}
                  severity={item.severity}
                  aria-controls={item.tabPanelId}
                  labelMaxWidth={maxWidths?.get(item.itemKey)}
                />
              ) : (
                <TabBarItem
                  itemKey={item.itemKey}
                  label={item.label}
                  icon={item.icon}
                  badge={item.badge}
                  metadata={item.metadata}
                  severity={item.severity}
                  aria-controls={item.tabPanelId}
                  labelMaxWidth={maxWidths?.get(item.itemKey)}
                />
              )
            )}
        {overflowItemKeys != null && overflowItemKeys.length > 0 && (
          <OverflowTabBarItem
            isOpen={isDropDownOpen}
            onClose={handleClose}
            overflowItemKey={overflowItemKey}
            overflowItems={items.filter((item) => overflowItemKeys?.includes(item.itemKey))}
          />
        )}
      </TabBar>
    </div>
  );
}
